// Define the user type
type User = {
	id: number;
	email: string;
	firstName?: string;
	surname?: string;
	role: string;
};

// Simple state management outside of React
class AuthController {
	private static instance: AuthController;
	private isAuthenticated = false;
	private isInitialized = false;
	private user: User | null = null;
	private authCheckInProgress = false;
	private isRefreshing = false; // New flag specifically for refresh operations
	private lastCheckTime = 0;
	private MIN_CHECK_INTERVAL = 2000; // Minimum 2 seconds between checks
	private listeners: Array<() => void> = [];

	private constructor() {}

	public static getInstance(): AuthController {
		if (!AuthController.instance) {
			AuthController.instance = new AuthController();
		}
		return AuthController.instance;
	}

	public getState() {
		return {
			isAuthenticated: this.isAuthenticated,
			isInitialized: this.isInitialized,
			user: this.user,
			isCheckingAuth: this.authCheckInProgress,
			isRefreshing: this.isRefreshing,
		};
	}

	public setAuthenticated(authenticated: boolean, user: User | null) {
		this.isAuthenticated = authenticated;
		this.isInitialized = true;
		this.user = user;
		this.notifyListeners();
	}

	public setCheckingAuth(checking: boolean) {
		this.authCheckInProgress = checking;
		this.notifyListeners();
	}

	public setRefreshing(refreshing: boolean) {
		this.isRefreshing = refreshing;
		this.notifyListeners();
	}

	// Regular auth check with time-based throttling
	public canCheckAuth(): boolean {
		const now = Date.now();
		if (now - this.lastCheckTime < this.MIN_CHECK_INTERVAL) {
			console.log("Auth check throttled - too soon since last check");
			return false;
		}

		if (this.authCheckInProgress) {
			console.log("Auth check already in progress");
			return false;
		}

		this.lastCheckTime = now;
		return true;
	}

	// Token refreshes - uses separate flag to prevent conflicts with regular auth checks
	public canRefreshToken(): boolean {
		// Only check if a refresh is already in progress
		if (this.isRefreshing) {
			console.log("Token refresh blocked - refresh already in progress");
			return false;
		}

		// Allow refresh regardless of when the last check was or if auth checks are in progress
		// This ensures refresh attempts aren't blocked by other auth activities
		return true;
	}

	public resetAuth() {
		this.isAuthenticated = false;
		this.user = null;
		this.isInitialized = true;
		this.authCheckInProgress = false;
		this.isRefreshing = false;
		this.notifyListeners();
	}

	public subscribe(listener: () => void) {
		this.listeners.push(listener);
		return () => {
			this.listeners = this.listeners.filter((l) => l !== listener);
		};
	}

	private notifyListeners() {
		this.listeners.forEach((listener) => listener());
	}

	public debugState() {
		console.table({
			isAuthenticated: this.isAuthenticated,
			isInitialized: this.isInitialized,
			hasUser: !!this.user,
			checkInProgress: this.authCheckInProgress,
			isRefreshing: this.isRefreshing,
			lastCheckTime: new Date(this.lastCheckTime).toISOString(),
		});
	}

	public forceAuthCheck(): boolean {
		// This method bypasses throttling for critical auth checks after refresh
		console.log("Forcing auth check after refresh (bypassing throttling)");
		this.lastCheckTime = Date.now(); // Update the timestamp
		return true;
	}
}

export const authController = AuthController.getInstance();

// Make authController available globally for services like Socket.IO
// This allows authentication to be managed consistently across the application
if (typeof window !== "undefined") {
	(window as any).authController = authController;
}
